import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import RichText from "../components/RichText";
import Footnotes from "../components/Footnotes";
import MetadataModal from "../components/MetadataModal";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { getTopic } from "../api/publicationsApi";
import { API_ORIGIN } from "../api/authApi";
import { localizeTitle } from "../utils/textDirection";

// Section A: public. One page per card (/publications/:slug/topics/:topicId),
// opened by clicking a card on the publication's detail page. Text-only
// content for now.
//
// Bilingual by design: title_fa/title_en etc. live together on the same
// row (see domain.Topic on the backend) — activeLang just picks which
// half of that single page is currently shown, with the LanguageSwitcher
// toggling it in place rather than navigating anywhere.
function TopicDetailPage() {
  const { slug, topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeLang, setActiveLang] = useState("fa");

  useEffect(() => {
    getTopic(topicId)
      .then((t) => {
        setTopic(t);
        setActiveLang(t.title_fa ? "fa" : "en");
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [topicId]);

  if (loading) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>در حال بارگذاری...</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !topic) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>این کارت یافت نشد.</p>
          <Link to={`/publications/${slug}`} className="back-link">
            ← بازگشت به نشریه
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const hasFa = Boolean(topic.title_fa);
  const hasEn = Boolean(topic.title_en);
  const title = (activeLang === "en" ? topic.title_en : topic.title_fa) || topic.title_fa || topic.title_en;
  const body = (activeLang === "en" ? topic.body_en : topic.body_fa) || topic.body_fa || topic.body_en;
  const footnotes = activeLang === "en" ? topic.footnotes_en : topic.footnotes_fa;

  const { dir: titleDir, text: titleText } = localizeTitle(title);
  const pageDir = activeLang === "en" ? "ltr" : "rtl";

  return (
    <div className="site">
      <SiteHeader />

      <main className="topic-detail" dir={pageDir} lang={activeLang}>
        <span className="corner corner-tr" />
        <span className="corner corner-tl" />
        <span className="corner corner-br" />
        <span className="corner corner-bl" />

        <Link to={`/publications/${slug}`} className="back-link">
          ← بازگشت به نشریه
        </Link>

        {topic.image_path && (
          <div className="topic-detail-image-frame">
            <img className="topic-detail-image" src={`${API_ORIGIN}${topic.image_path}`} alt={title} />
          </div>
        )}

        <header className="topic-detail-header">
          <div className="topic-detail-kicker">
            <span className="topic-detail-kicker-seg" />
            <span className="topic-detail-kicker-label">{topic.category}</span>
            <span className="topic-detail-kicker-seg" />
          </div>

          <h1 className="topic-detail-title" dir={titleDir}>{titleText}</h1>
        </header>

        <div className="content-metadata-row">
          <MetadataModal metadata={topic.metadata} />
          <LanguageSwitcher activeLang={activeLang} onChange={setActiveLang} hasFa={hasFa} hasEn={hasEn} />
        </div>

        {body ? (
          <RichText className="topic-detail-body" html={body} />
        ) : (
          <p className="topic-detail-body topic-detail-body--empty">
            هنوز متنی برای این کارت ثبت نشده است.
          </p>
        )}

        <Footnotes footnotes={footnotes} />

        <div className="topic-detail-end-mark" aria-hidden="true">
          ❖
        </div>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}

export default TopicDetailPage;
